/**
 * PDF regenerate — re-render and upload PDF sidecars for every study. Backfill
 * for studies that landed via sync/migration/bulk-import without ever hitting
 * the save path that generates PDFs.
 */

import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import {
  regenerateStudyPdfs,
  type PdfRegenerateProgress,
  type PdfRegenerateResult,
} from '@/state/persistence';
import { bibleState } from '@/state/bible-state';

export const PdfRegenerateSection = observer(function PdfRegenerateSection() {
  const [progress, setProgress] = useState<PdfRegenerateProgress | null>(null);
  const [result, setResult] = useState<PdfRegenerateResult | null>(null);
  const [isWorking, setWorking] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const studyCount = bibleState.studies.length;

  const handleRun = async () => {
    if (studyCount === 0) return;
    setWorking(true);
    setResult(null);
    setProgress({
      studyIndex: 0,
      totalStudies: studyCount,
      studyName: '',
      generated: 0,
      errors: 0,
    });
    abortRef.current = new AbortController();
    try {
      const out = await regenerateStudyPdfs(bibleState.studies, {
        onProgress: (event) => setProgress(event),
        signal: abortRef.current.signal,
      });
      setResult(out);
    } finally {
      setWorking(false);
      abortRef.current = null;
    }
  };

  const handleCancel = () => abortRef.current?.abort();

  return (
    <section className="border-t border-stone-200/50 pt-4 dark:border-stone-700/50">
      <h2 className="mb-1 text-lg font-semibold text-stone-800 dark:text-stone-200">
        Regenerate Study PDFs
      </h2>
      <p className="mb-3 text-xs text-stone-500 dark:text-stone-400">
        Re-render and upload PDF sidecars for every study. Useful after a bulk
        import (PDFs are skipped for speed) or for studies that were synced from
        another device. Sequential — roughly a few seconds per study.
      </p>

      <div className="flex gap-2">
        <button
          onClick={handleRun}
          disabled={isWorking || studyCount === 0}
          className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
        >
          {isWorking
            ? 'Regenerating…'
            : `Regenerate ${studyCount} PDF${studyCount === 1 ? '' : 's'}`}
        </button>
        {isWorking && (
          <button
            onClick={handleCancel}
            className="rounded-lg border border-stone-300 px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-100 dark:border-stone-600 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            Cancel
          </button>
        )}
      </div>

      {progress && (
        <div className="mt-3 rounded-lg bg-stone-100 p-3 text-xs text-stone-600 dark:bg-stone-800 dark:text-stone-400">
          <div className="mb-1 font-mono">
            [{progress.studyIndex}/{progress.totalStudies}]{' '}
            {progress.studyName || (isWorking ? 'starting…' : 'done')}
          </div>
          <div className="font-mono">
            generated: {progress.generated} · errors: {progress.errors}
          </div>
        </div>
      )}

      {result && (
        <div className="mt-3 rounded-lg bg-stone-100 p-3 text-xs text-stone-700 dark:bg-stone-800 dark:text-stone-300">
          <div className="mb-1 font-medium">Regenerate complete.</div>
          <ul className="space-y-0.5">
            <li>PDFs generated: {result.generated}</li>
            <li>Errors: {result.errors.length}</li>
          </ul>
          {result.errors.length > 0 && (
            <button
              onClick={() => setShowErrors((v) => !v)}
              className="mt-2 underline"
            >
              {showErrors ? 'Hide errors' : 'Show errors'}
            </button>
          )}
          {showErrors && result.errors.length > 0 && (
            <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded bg-stone-200/60 p-2 font-mono dark:bg-stone-700/60">
              {result.errors.map((e, i) => (
                <li key={i}>
                  <strong>{e.studyName}</strong>: {e.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
});
